/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { ticketsAPI, locationsAPI } from '../lib/api';
import { formatDateTime, formatRelativeTime, TICKET_STATUS, TICKET_TYPES, TICKET_PRIORITY, PROJECT_CATEGORIES, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Search, Filter, FileText, Eye, MessageSquare, Clock, CheckCircle,
  AlertCircle, Loader2, ChevronLeft, ChevronRight, Send, Star, Forward,
  Upload, X, Download, ThumbsUp, Lightbulb, ShieldCheck, Paperclip,
  Printer, User, MapPin, Tag, CalendarDays, Hash, Trash2,
} from 'lucide-react';

// =================== LIST PAGE ===================
const TicketsListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Vatandas Katilim');
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 10;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const isAdmin = ['admin', 'genel_sekreter', 'kent_konseyi_baskani'].includes(user?.role);
  const canManageTickets = isAdmin || ['meclis_baskani', 'genel_sekreter_yardimcisi'].includes(user?.role);
  const canSeePersonalInfo = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi', 'meclis_baskani'].includes(user?.role);

  useEffect(() => { loadTickets(); loadCategories(); }, [page, statusFilter, typeFilter, categoryFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * limit, limit, search: searchTerm || undefined, status: statusFilter || undefined, type: typeFilter || undefined, category: categoryFilter || undefined };
      const response = await ticketsAPI.getAll(params);
      setTickets(response.data.tickets);
      setTotal(response.data.total);
    } catch { toast.error('Talepler yüklenirken hata oluştu'); }
    finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try { const response = await ticketsAPI.getCategories(); setCategories(response.data); } catch {}
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadTickets(); };

  const handleBulkAction = async (action, status) => {
    if (selectedIds.length === 0) { toast.error('Lütfen en az bir talep seçin'); return; }
    try {
      await ticketsAPI.bulkAction({ ticket_ids: selectedIds, action, status });
      toast.success(`${selectedIds.length} talep güncellendi`);
      setSelectedIds([]);
      loadTickets();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'İşlem başarısız'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === tickets.length ? [] : tickets.map(t => t.id));
  };

  const getStatusBadge = (status) => {
    const info = TICKET_STATUS[status] || { label: status, color: 'default' };
    const cc = { success: 'bg-emerald-50 text-emerald-700 border-emerald-200', warning: 'bg-amber-50 text-amber-700 border-amber-200', error: 'bg-red-50 text-red-700 border-red-200', info: 'bg-blue-50 text-blue-700 border-blue-200', default: 'bg-slate-50 text-slate-700 border-slate-200', purple: 'bg-purple-50 text-purple-700 border-purple-200' };
    return <Badge className={`${cc[info.color]} border`}>{info.label}</Badge>;
  };
  const getTypeBadge = (type) => {
    const info = TICKET_TYPES[type] || { label: type, color: 'default' };
    const cc = { success: 'bg-emerald-50 text-emerald-700 border-emerald-200', warning: 'bg-amber-50 text-amber-700 border-amber-200', info: 'bg-blue-50 text-blue-700 border-blue-200', default: 'bg-slate-50 text-slate-700 border-slate-200', purple: 'bg-purple-50 text-purple-700 border-purple-200' };
    return <Badge variant="outline" className={`${cc[info.color]} border`}>{info.label}</Badge>;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="tickets-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vatandaş Katılım</h1>
          <p className="text-slate-500">Talep, şikayet, öneri ve proje önerilerinizi yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/panel/talepler/yeni')} data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-2" /> Yeni Talep / Öneri
          </Button>
          <Button onClick={() => navigate('/panel/talepler/proje-onerisi')} variant="outline" data-testid="new-project-btn">
            <Lightbulb className="w-4 h-4 mr-2" /> Proje Önerisi
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-shadow">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Ara (ID, konu, açıklama)..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="ticket-search-input" />
              </div>
            </div>
            <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="type-filter"><SelectValue placeholder="Tür" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {Object.entries(TICKET_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {Object.entries(TICKET_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={categoryFilter || "all"} onValueChange={(v) => { setCategoryFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="category-filter"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {categories.map(cat => <SelectItem key={cat.code} value={cat.code}>{cat.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" variant="secondary"><Filter className="w-4 h-4 mr-2" /> Filtrele</Button>
          </form>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {isAdmin && selectedIds.length > 0 && (
        <Card className="card-shadow border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">{selectedIds.length} talep seçili</span>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleBulkAction('status_change', 'inceleniyor')} data-testid="bulk-review-btn">İncele</Button>
              <Button size="sm" variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" onClick={() => handleBulkAction('status_change', 'cozuldu')}>Çözüldü</Button>
              <Button size="sm" variant="outline" className="bg-red-50 text-red-700 border-red-200" onClick={() => handleBulkAction('status_change', 'kapatildi')}>Kapat</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Seçimi Kaldır</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="card-shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
          ) : tickets.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {isAdmin && <TableHead className="w-10"><Checkbox checked={selectedIds.length === tickets.length && tickets.length > 0} onCheckedChange={toggleSelectAll} /></TableHead>}
                  <TableHead className="font-semibold">No</TableHead>
                  <TableHead className="font-semibold">Tür</TableHead>
                  <TableHead className="font-semibold">Konu</TableHead>
                  {canManageTickets && <TableHead className="font-semibold">Atanan Organ</TableHead>}
                  <TableHead className="font-semibold">Durum</TableHead>
                  {canManageTickets && <TableHead className="font-semibold">Yönlendirme</TableHead>}
                  <TableHead className="font-semibold">Öncelik</TableHead>
                  <TableHead className="font-semibold">Tarih</TableHead>
                  <TableHead className="font-semibold text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map(ticket => (
                  <TableRow key={ticket.id} className="hover:bg-slate-50/80 cursor-pointer" onClick={() => navigate(`/panel/talepler/${ticket.id}`)} data-testid={`ticket-row-${ticket.id}`}>
                    {isAdmin && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedIds.includes(ticket.id)} onCheckedChange={() => toggleSelect(ticket.id)} /></TableCell>}
                    <TableCell className="font-mono text-sm">{ticket.ticket_no}</TableCell>
                    <TableCell>{getTypeBadge(ticket.type)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900 truncate max-w-xs">{ticket.subject}</p>
                        <p className="text-xs text-slate-500">{ticket.created_by_name}</p>
                        {canSeePersonalInfo && ticket.created_by_email && (
                          <p className="text-[11px] text-slate-400 truncate max-w-xs" data-testid={`ticket-creator-email-${ticket.id}`}>
                            <span className="font-mono">{ticket.created_by_email}</span>
                            {ticket.created_by_tc_masked && (
                              <span className="ml-2 text-slate-400">· TC: <span className="font-mono">{ticket.created_by_tc_masked}</span></span>
                            )}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    {canManageTickets && (
                      <TableCell>
                        {ticket.assigned_organ_name ? (
                          <Badge className={`text-xs ${ticket.assigned_organ_type === 'calisma_grubu' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'} border`}>
                            {ticket.assigned_organ_name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">Atanmamış</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                    {canManageTickets && (
                      <TableCell>
                        <div className="flex items-center gap-1" title={`${ticket.forward_count || 0}/3 yönlendirme`}>
                          {[0, 1, 2].map(i => (
                            <span key={`fw-${i}`} className={`w-2 h-2 rounded-full ${i < (ticket.forward_count || 0) ? 'bg-amber-500' : 'bg-slate-200'}`} />
                          ))}
                          <span className="text-xs text-slate-500 ml-1">{ticket.forward_count || 0}/3</span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell><Badge variant="outline">{TICKET_PRIORITY[ticket.priority]?.label || 'Normal'}</Badge></TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDateTime(ticket.created_at)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/panel/talepler/${ticket.id}`)} data-testid={`view-ticket-${ticket.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Henüz talep yok</h3>
              <p className="text-slate-500 mt-1">Yeni talep veya proje önerisi oluşturun</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Toplam {total} kayıt</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm text-slate-700">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

// =================== NEW TICKET PAGE (Talep/Şikayet/Öneri) ===================
const NewTicketPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Yeni Talep');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [mahalleler, setMahalleler] = useState([]);
  const fileInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  const [formData, setFormData] = useState({
    type: 'talep', category: '', subject: '', description: '',
    il_id: '', ilce_id: '', mahalle_id: '', sokak: '', kvkk_onay: false,
  });

  useEffect(() => {
    loadCategories();
    loadIller();
  }, []);

  useEffect(() => {
    if (formData.il_id) { loadIlceler(formData.il_id); setFormData(p => ({ ...p, ilce_id: '', mahalle_id: '' })); setMahalleler([]); }
  }, [formData.il_id]);
  useEffect(() => {
    if (formData.ilce_id) loadMahalleler(formData.ilce_id);
  }, [formData.ilce_id]);

  const loadCategories = async () => { try { const r = await ticketsAPI.getCategories(); setCategories(r.data); } catch {} };
  const loadIller = async () => {
    try {
      const r = await locationsAPI.getIller(); setIller(r.data);
      const adana = r.data.find(il => il.name === 'Adana');
      if (adana) setFormData(p => ({ ...p, il_id: adana.id }));
    } catch {}
  };
  const loadIlceler = async (ilId) => { try { const r = await locationsAPI.getIlceler(ilId); setIlceler(r.data); } catch {} };
  const loadMahalleler = async (ilceId) => { try { const r = await locationsAPI.getMahalleler(ilceId); setMahalleler(r.data); } catch {} };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (pendingFiles.length + files.length > 5) { toast.error('En fazla 5 dosya yükleyebilirsiniz'); return; }
    const valid = files.filter(f => f.size <= 50 * 1024 * 1024);
    if (valid.length < files.length) toast.error('50MB üzeri dosyalar eklenmedi');
    setPendingFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.kvkk_onay) { toast.error('KVKK onayını kabul etmeniz gerekmektedir'); return; }
    setLoading(true);
    try {
      const response = await ticketsAPI.create(formData);
      const ticketId = response.data.id;
      // Upload files
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append('file', file);
        try { await ticketsAPI.uploadFile(ticketId, fd); } catch { toast.error(`${file.name} yüklenemedi`); }
      }
      toast.success('Talebiniz başarıyla oluşturuldu');
      navigate(`/panel/talepler/${ticketId}`);
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Talep oluşturulamadı'); }
    finally { setLoading(false); }
  };

  const typeLabels = { talep: 'Talep', sikayet: 'Sikayet', oneri: 'Oneri' };
  const typeColors = {
    talep: { bg: 'border-blue-500 bg-blue-50 text-blue-800', dot: 'bg-blue-500' },
    sikayet: { bg: 'border-red-500 bg-red-50 text-red-800', dot: 'bg-red-500' },
    oneri: { bg: 'border-emerald-500 bg-emerald-50 text-emerald-800', dot: 'bg-emerald-500' },
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-ticket-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/talepler')} className="text-slate-600 hover:text-slate-900">
          <ChevronLeft className="w-4 h-4 mr-1" /> Taleplere Don
        </Button>
      </div>

      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-emerald-800 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Yeni Talep / Sikayet / Oneri</h1>
              <p className="text-sm text-white/50">Formu eksiksiz doldurun ve gonderin</p>
            </div>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol - Ana Form */}
          <div className="lg:col-span-2 space-y-5">
            {/* Talep Turu */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                  Talep Turu
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Basvurunuzun turunu secin</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {['talep', 'sikayet', 'oneri'].map((t) => {
                    const selected = formData.type === t;
                    const colors = typeColors[t];
                    return (
                      <button key={t} type="button" onClick={() => setFormData({ ...formData, type: t })}
                        data-testid={`type-${t}`}
                        className={`flex items-center justify-center gap-2.5 px-3 py-3 rounded-lg border text-sm font-medium transition-all ${selected ? colors.bg : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${selected ? colors.dot : 'bg-slate-300'}`} />
                        {typeLabels[t]}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Icerik */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                  Icerik
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="subject" className="text-sm font-medium">Konu *</Label>
                  <Input id="subject" placeholder="Talebinizin konusu" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required data-testid="subject-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="description" className="text-sm font-medium">Aciklama / Detay *</Label>
                  <Textarea id="description" placeholder="Talebinizi detayli bir sekilde aciklayin (en az 20 karakter)..." rows={6} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="description-input" className="mt-1.5" />
                  <p className="text-xs text-slate-400 mt-1">{formData.description.length} karakter</p>
                </div>
              </CardContent>
            </Card>

            {/* Konum Bilgileri */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                  Konum Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Il *</Label>
                    <Select value={formData.il_id} onValueChange={(v) => setFormData({ ...formData, il_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Il secin" /></SelectTrigger>
                      <SelectContent>{iller.map(il => <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Ilce *</Label>
                    <Select value={formData.ilce_id} onValueChange={(v) => setFormData({ ...formData, ilce_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Ilce secin" /></SelectTrigger>
                      <SelectContent>{ilceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {mahalleler.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium">Mahalle</Label>
                      <Select value={formData.mahalle_id} onValueChange={(v) => setFormData({ ...formData, mahalle_id: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Mahalle secin" /></SelectTrigger>
                        <SelectContent>{mahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium">Sokak (Opsiyonel)</Label>
                    <Input placeholder="Sokak adi" value={formData.sokak} onChange={(e) => setFormData({ ...formData, sokak: e.target.value })} className="mt-1.5" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dosya & KVKK */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">4</span>
                  Dosya ve Onay
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Dosya Ekle (En fazla 5, max 50MB)</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors mt-1.5" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-7 h-7 text-slate-400 mx-auto mb-1.5" />
                    <p className="text-sm text-slate-500">Dosya secmek icin tiklayin</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</p>
                  </div>
                  <input ref={fileInputRef} type="file" className="hidden" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleFileChange} data-testid="file-upload-input" />
                  {pendingFiles.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {pendingFiles.map((f, idx) => (
                        <div key={`file-${idx}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Paperclip className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm">{f.name}</span>
                            <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeFile(idx)}><X className="w-3 h-3" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-lg border">
                  <Checkbox id="kvkk" checked={formData.kvkk_onay} onCheckedChange={(v) => setFormData({ ...formData, kvkk_onay: !!v })} data-testid="kvkk-checkbox" />
                  <div>
                    <Label htmlFor="kvkk" className="text-sm leading-relaxed cursor-pointer">
                      Kisisel Verilerin Korunmasi Kanunu (KVKK) kapsaminda verilerimin islenmesini kabul ediyorum. *
                    </Label>
                    <p className="text-xs text-slate-400 mt-1">Paylastiginiz bilgiler yalnizca talebinizin degerlendirilmesi amaciyla kullanilacaktir.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sag - Sidebar */}
          <div className="space-y-5">
            {/* Kisisel Bilgiler */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-600">
                  <User className="w-4 h-4" /> Basvuran
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-slate-400">Ad Soyad</p>
                  <p className="text-sm font-medium text-slate-800">{user?.name || ''} {user?.surname || ''}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">E-posta</p>
                  <p className="text-sm font-medium text-slate-800 truncate">{user?.email || ''}</p>
                </div>
              </CardContent>
            </Card>

            {/* Kategori */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-600">
                  <Tag className="w-4 h-4" /> Alt Kategori
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="category-select"><SelectValue placeholder="Kategori secin" /></SelectTrigger>
                  <SelectContent>{categories.map(cat => <SelectItem key={cat.code} value={cat.code}>{cat.name}</SelectItem>)}</SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Onizleme */}
            <Card className="shadow-sm border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-500">Onizleme</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-8 rounded-full ${formData.type === 'sikayet' ? 'bg-red-500' : formData.type === 'oneri' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{formData.subject || 'Konu...'}</p>
                      <p className="text-xs text-slate-400 truncate">{formData.description ? formData.description.substring(0, 60) + '...' : 'Aciklama...'}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge variant="outline" className="text-xs h-5 px-1.5">{typeLabels[formData.type]}</Badge>
                    {formData.category && <Badge variant="outline" className="text-xs h-5 px-1.5">{categories.find(c => c.code === formData.category)?.name || formData.category}</Badge>}
                  </div>
                  {pendingFiles.length > 0 && (
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {pendingFiles.length} dosya eklendi</p>
                  )}
                  <div className={`text-xs mt-1 ${formData.kvkk_onay ? 'text-emerald-600' : 'text-red-400'}`}>
                    {formData.kvkk_onay ? 'KVKK onayi verildi' : 'KVKK onayi gerekli'}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-2">
              <Button type="submit" disabled={loading} className="w-full shadow-sm bg-emerald-600 hover:bg-emerald-700" data-testid="submit-ticket-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Gonder
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/panel/talepler')} className="w-full">
                Iptal
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// =================== NEW PROJECT PROPOSAL PAGE ===================
const NewProjectPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Yeni Proje Onerisi');
  const [loading, setLoading] = useState(false);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [mahalleler, setMahalleler] = useState([]);
  const fileInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  const [formData, setFormData] = useState({
    type: 'proje', category: '', subject: '', description: '',
    il_id: '', ilce_id: '', mahalle_id: '',
    tahmini_maliyet: '', tahmini_sure: '', isbirligi_kurumlar: '', kvkk_onay: false,
  });

  useEffect(() => { loadIller(); }, []);
  useEffect(() => { if (formData.il_id) { loadIlceler(formData.il_id); setFormData(p => ({ ...p, ilce_id: '', mahalle_id: '' })); setMahalleler([]); } }, [formData.il_id]);
  useEffect(() => { if (formData.ilce_id) loadMahalleler(formData.ilce_id); }, [formData.ilce_id]);

  const loadIller = async () => {
    try { const r = await locationsAPI.getIller(); setIller(r.data); const a = r.data.find(i => i.name === 'Adana'); if (a) setFormData(p => ({ ...p, il_id: a.id })); } catch {}
  };
  const loadIlceler = async (id) => { try { const r = await locationsAPI.getIlceler(id); setIlceler(r.data); } catch {} };
  const loadMahalleler = async (id) => { try { const r = await locationsAPI.getMahalleler(id); setMahalleler(r.data); } catch {} };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (pendingFiles.length + files.length > 5) { toast.error('En fazla 5 dosya'); return; }
    setPendingFiles(prev => [...prev, ...files.filter(f => f.size <= 50 * 1024 * 1024)]);
  };
  const removeFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.kvkk_onay) { toast.error('KVKK onayını kabul edin'); return; }
    if (!formData.category) { toast.error('Proje kategorisi seçin'); return; }
    setLoading(true);
    try {
      const response = await ticketsAPI.create(formData);
      const ticketId = response.data.id;
      for (const file of pendingFiles) {
        const fd = new FormData(); fd.append('file', file);
        try { await ticketsAPI.uploadFile(ticketId, fd); } catch {}
      }
      toast.success('Proje öneriniz başarıyla gönderildi');
      navigate(`/panel/talepler/${ticketId}`);
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Proje önerisi oluşturulamadı'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-project-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/talepler')} className="text-slate-600 hover:text-slate-900">
          <ChevronLeft className="w-4 h-4 mr-1" /> Taleplere Don
        </Button>
      </div>

      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-amber-700 via-orange-800 to-amber-700 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Proje Onerisi</h1>
              <p className="text-sm text-white/50">Sehriniz icin yeni bir proje fikri onerin</p>
            </div>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol - Ana Form */}
          <div className="lg:col-span-2 space-y-5">
            {/* Proje Bilgileri */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                  Proje Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="prj-title" className="text-sm font-medium">Proje Basligi *</Label>
                  <Input id="prj-title" placeholder="Projenizin basligi" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required data-testid="project-title-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="prj-desc" className="text-sm font-medium">Proje Detayi *</Label>
                  <Textarea id="prj-desc" placeholder="Projenizi detayli bir sekilde aciklayin: amaci, hedefi, kapsami..." rows={8} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="project-description-input" className="mt-1.5" />
                  <p className="text-xs text-slate-400 mt-1">{formData.description.length} karakter</p>
                </div>
              </CardContent>
            </Card>

            {/* Konum Bilgileri */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                  Konum Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Il</Label>
                    <Select value={formData.il_id} onValueChange={(v) => setFormData({ ...formData, il_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Il" /></SelectTrigger>
                      <SelectContent>{iller.map(il => <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Ilce</Label>
                    <Select value={formData.ilce_id} onValueChange={(v) => setFormData({ ...formData, ilce_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Ilce" /></SelectTrigger>
                      <SelectContent>{ilceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {mahalleler.length > 0 && (
                  <div className="mt-4">
                    <Label className="text-sm font-medium">Mahalle</Label>
                    <Select value={formData.mahalle_id} onValueChange={(v) => setFormData({ ...formData, mahalle_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Mahalle" /></SelectTrigger>
                      <SelectContent>{mahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tahminler & Isbirligi */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                  Proje Tahminleri
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Bu alanlar opsiyoneldir</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Tahmini Maliyet</Label>
                    <Input placeholder="Or: 500.000 TL" value={formData.tahmini_maliyet} onChange={(e) => setFormData({ ...formData, tahmini_maliyet: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Tahmini Sure</Label>
                    <Input placeholder="Or: 6 ay" value={formData.tahmini_sure} onChange={(e) => setFormData({ ...formData, tahmini_sure: e.target.value })} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Isbirligi Yapilacak STK / Kurum</Label>
                  <Input placeholder="Or: TEMA Vakfi, Belediye Cevre Mudurlugu" value={formData.isbirligi_kurumlar} onChange={(e) => setFormData({ ...formData, isbirligi_kurumlar: e.target.value })} className="mt-1.5" />
                </div>
              </CardContent>
            </Card>

            {/* Dosya & KVKK */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-bold">4</span>
                  Dosya ve Onay
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Dosya Ekle (Cizim, plan, rapor, gorsel sunum)</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors mt-1.5" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-7 h-7 text-slate-400 mx-auto mb-1.5" />
                    <p className="text-sm text-slate-500">Dosya secmek icin tiklayin</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 50MB)</p>
                  </div>
                  <input ref={fileInputRef} type="file" className="hidden" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleFileChange} />
                  {pendingFiles.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {pendingFiles.map((f, idx) => (
                        <div key={`kvkk-${idx}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Paperclip className="w-4 h-4 text-amber-600" />
                            <span className="text-sm">{f.name}</span>
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeFile(idx)}><X className="w-3 h-3" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-lg border">
                  <Checkbox id="kvkk_prj" checked={formData.kvkk_onay} onCheckedChange={(v) => setFormData({ ...formData, kvkk_onay: !!v })} data-testid="kvkk-checkbox-project" />
                  <Label htmlFor="kvkk_prj" className="text-sm leading-relaxed cursor-pointer">
                    KVKK kapsaminda verilerimin islenmesini kabul ediyorum. *
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sag - Sidebar */}
          <div className="space-y-5">
            {/* Proje Kategorisi */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-600">
                  <Tag className="w-4 h-4" /> Proje Kategorisi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {PROJECT_CATEGORIES.map((cat) => {
                    const selected = formData.category === cat.value;
                    return (
                      <button key={cat.value} type="button" onClick={() => setFormData({ ...formData, category: cat.value })}
                        data-testid={`project-cat-${cat.value}`}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border text-sm text-left transition-all ${selected ? 'border-amber-500 bg-amber-50 text-amber-800 font-medium' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selected ? 'bg-amber-500' : 'bg-slate-300'}`} />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Ozet */}
            <Card className="shadow-sm border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-500">Ozet</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-8 rounded-full bg-amber-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{formData.subject || 'Proje basligi...'}</p>
                      <p className="text-xs text-slate-400 truncate">{formData.description ? formData.description.substring(0, 60) + '...' : 'Detay...'}</p>
                    </div>
                  </div>
                  {formData.category && (
                    <Badge variant="outline" className="text-xs">{PROJECT_CATEGORIES.find(c => c.value === formData.category)?.label || formData.category}</Badge>
                  )}
                  {formData.tahmini_maliyet && (
                    <p className="text-xs text-slate-500">Maliyet: {formData.tahmini_maliyet}</p>
                  )}
                  {formData.tahmini_sure && (
                    <p className="text-xs text-slate-500">Sure: {formData.tahmini_sure}</p>
                  )}
                  {pendingFiles.length > 0 && (
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {pendingFiles.length} dosya</p>
                  )}
                  <div className={`text-xs mt-1 ${formData.kvkk_onay ? 'text-emerald-600' : 'text-red-400'}`}>
                    {formData.kvkk_onay ? 'KVKK onayi verildi' : 'KVKK onayi gerekli'}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-2">
              <Button type="submit" disabled={loading} className="w-full shadow-sm bg-amber-600 hover:bg-amber-700" data-testid="submit-project-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lightbulb className="w-4 h-4 mr-2" />}
                Projeyi Gonder
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/panel/talepler')} className="w-full">
                Iptal
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// =================== TICKET DETAIL PAGE ===================
const TicketDetailPage = ({ ticketId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Talep Detay');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [discussion, setDiscussion] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingDiscussion, setSubmittingDiscussion] = useState(false);
  const [satisfaction, setSatisfaction] = useState(0);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [forwardData, setForwardData] = useState({ assigned_organ_id: '', note: '' });
  const [forwarding, setForwarding] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const fileInputRef = useRef(null);

  const isAdmin = ['admin', 'genel_sekreter', 'kent_konseyi_baskani'].includes(user?.role);
  const canManageTickets = isAdmin || ['meclis_baskani', 'genel_sekreter_yardimcisi'].includes(user?.role);
  const canSeePersonalInfo = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi', 'meclis_baskani'].includes(user?.role);
  // Kullanici detay sayfasina gidebilen roller (meclis_baskani haric)
  const canViewUserDetail = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'].includes(user?.role);
  const isProject = ticket?.type === 'proje';
  const userLiked = ticket?.likes?.includes(user?.id);

  useEffect(() => { loadTicket(); if (canManageTickets) loadInstitutions(); }, [ticketId]);
  const loadTicket = async () => { try { const r = await ticketsAPI.getById(ticketId); setTicket(r.data); } catch { toast.error('Talep yüklenirken hata oluştu'); navigate('/panel/talepler'); } finally { setLoading(false); } };
  const loadInstitutions = async () => {
    setLoadingGroups(true);
    try { const r = await ticketsAPI.getInstitutions(); setInstitutions(r.data); }
    catch {}
    finally { setLoadingGroups(false); }
  };

  // Build hierarchy: meclisler with their child calisma_gruplari
  const organHierarchy = React.useMemo(() => {
    const meclisler = institutions.filter(i => i.type === 'meclis' && i.name);
    const calismaGruplari = institutions.filter(i => i.type === 'calisma_grubu' && i.name);
    return meclisler.map(m => ({
      ...m,
      children: calismaGruplari.filter(cg => cg.parent_id === m.id),
    }));
  }, [institutions]);

  const handleAddComment = async (e) => {
    e.preventDefault(); if (!comment.trim()) return;
    setSubmittingComment(true);
    try { await ticketsAPI.addComment(ticketId, { content: comment }); toast.success('Yorumunuz eklendi'); setComment(''); loadTicket(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Yorum eklenemedi'); }
    finally { setSubmittingComment(false); }
  };

  const handleAddDiscussion = async (e) => {
    e.preventDefault(); if (!discussion.trim()) return;
    setSubmittingDiscussion(true);
    try { await ticketsAPI.addDiscussion(ticketId, { content: discussion }); toast.success('Tartışma yorumunuz eklendi'); setDiscussion(''); loadTicket(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Yorum eklenemedi'); }
    finally { setSubmittingDiscussion(false); }
  };

  const handleRateSatisfaction = async (score) => {
    try { await ticketsAPI.rateSatisfaction(ticketId, score); toast.success('Puanınız kaydedildi'); setSatisfaction(score); loadTicket(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Puan verilemedi'); }
  };

  const handleForward = async () => {
    if (!forwardData.assigned_organ_id) { toast.error('Hedef organ seçin'); return; }
    setForwarding(true);
    try { await ticketsAPI.forward(ticketId, forwardData); toast.success('Talep yönlendirildi'); setForwardDialogOpen(false); setForwardData({ assigned_organ_id: '', note: '' }); loadTicket(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Yönlendirme başarısız'); }
    finally { setForwarding(false); }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    try { await ticketsAPI.update(ticketId, { status: newStatus }); toast.success('Durum güncellendi'); setStatusDialogOpen(false); loadTicket(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Güncelleme başarısız'); }
  };

  const handleLike = async () => {
    try { const r = await ticketsAPI.toggleLike(ticketId); toast.success(r.data.action === 'liked' ? 'Beğendiniz' : 'Beğeni kaldırıldı'); loadTicket(); }
    catch (error) { toast.error('İşlem başarısız'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { await ticketsAPI.uploadFile(ticketId, fd); toast.success('Dosya yüklendi'); loadTicket(); }
    catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Dosya yüklenemedi'); }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusBadge = (status) => {
    const info = TICKET_STATUS[status] || { label: status, color: 'default' };
    const cc = {
      success: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      warning: 'bg-amber-100 text-amber-800 border-amber-300',
      error: 'bg-red-100 text-red-800 border-red-300',
      info: 'bg-blue-100 text-blue-800 border-blue-300',
      default: 'bg-slate-100 text-slate-800 border-slate-300',
      purple: 'bg-purple-100 text-purple-800 border-purple-300',
    };
    return <Badge className={`${cc[info.color]} border px-3 py-1 text-xs font-semibold`}>{info.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const info = TICKET_TYPES[type] || { label: type, color: 'default' };
    const cc = {
      success: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      warning: 'bg-amber-100 text-amber-800 border-amber-300',
      info: 'bg-blue-100 text-blue-800 border-blue-300',
      default: 'bg-slate-100 text-slate-800 border-slate-300',
      purple: 'bg-purple-100 text-purple-800 border-purple-300',
    };
    return <Badge className={`${cc[info.color]} border px-3 py-1 text-xs font-semibold`}>{info.label}</Badge>;
  };

  const getPriorityColor = (priority) => {
    const colors = { acil: 'text-red-600', yuksek: 'text-orange-600', normal: 'text-blue-600', dusuk: 'text-slate-500' };
    return colors[priority] || 'text-blue-600';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!ticket) return null;

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print { display: none !important; }
          .print-break { page-break-inside: avoid; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto space-y-6 print-area" data-testid="ticket-detail-page">
        {/* Top Navigation Bar */}
        <div className="flex items-center justify-between no-print">
          <Button variant="ghost" size="sm" onClick={() => navigate('/panel/talepler')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Taleplere Dön
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" /> Yazdır
            </Button>
            {canManageTickets && !['cozuldu', 'kapatildi', 'iptal'].includes(ticket.status) && (
              <>
                <Button variant="outline" size="sm" onClick={() => setStatusDialogOpen(true)} data-testid="change-status-btn">Durum Değiştir</Button>
                <Button size="sm" onClick={() => setForwardDialogOpen(true)} data-testid="forward-ticket-btn"><Forward className="w-4 h-4 mr-2" /> Yönlendir</Button>
              </>
            )}
          </div>
        </div>

        {/* Hero Card - Ticket Header */}
        <Card className="overflow-hidden border-0 shadow-lg print-break">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-slate-400 font-mono text-sm flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5" /> {ticket.ticket_no}
                  </span>
                  {getTypeBadge(ticket.type)}
                  {getStatusBadge(ticket.status)}
                </div>
                <h1 className="text-xl font-bold text-white">{ticket.subject}</h1>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {ticket.created_by_name}</span>
                  <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {formatDateTime(ticket.created_at)}</span>
                  {ticket.category && <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {ticket.category}</span>}
                </div>
              </div>
              <div className="text-right hidden sm:block">
                <p className={`text-sm font-semibold ${getPriorityColor(ticket.priority)}`}>
                  {TICKET_PRIORITY[ticket.priority]?.label || 'Normal'} Öncelik
                </p>
              </div>
            </div>
          </div>

          {/* Forwarded Info */}
          {(ticket.assigned_organ_name || ticket.assigned_kurum) && (
            <div className="bg-blue-50 border-b border-blue-100 px-6 py-3 flex items-center gap-3">
              <Forward className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-blue-900">Yönlendirilen: </span>
                <Badge className={`text-xs ${ticket.assigned_organ_type === 'calisma_grubu' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-100 text-blue-800 border-blue-300'} border`}>
                  {ticket.assigned_organ_name || ticket.assigned_kurum}
                </Badge>
                {ticket.forward_count > 0 && (
                  <div className="flex items-center gap-1 ml-2">
                    {[0, 1, 2].map(i => (
                      <span key={`fwd-${i}`} className={`w-2 h-2 rounded-full ${i < ticket.forward_count ? 'bg-amber-500' : 'bg-slate-200'}`} />
                    ))}
                    <span className="text-xs text-blue-600 ml-1">{ticket.forward_count}/3</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="shadow-sm print-break">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  {isProject ? 'Proje Detayı' : 'Açıklama'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
              </CardContent>
            </Card>

            {/* Project extras */}
            {isProject && (ticket.tahmini_maliyet || ticket.tahmini_sure || ticket.isbirligi_kurumlar) && (
              <Card className="shadow-sm print-break">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" /> Proje Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {ticket.tahmini_maliyet && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Tahmini Maliyet</p>
                        <p className="text-slate-900 font-semibold">{ticket.tahmini_maliyet}</p>
                      </div>
                    )}
                    {ticket.tahmini_sure && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Tahmini Süre</p>
                        <p className="text-slate-900 font-semibold">{ticket.tahmini_sure}</p>
                      </div>
                    )}
                    {ticket.isbirligi_kurumlar && (
                      <div className="bg-slate-50 rounded-lg p-3 sm:col-span-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">İşbirliği Kurumları</p>
                        <p className="text-slate-900">{ticket.isbirligi_kurumlar}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Files */}
            {(ticket.files?.length > 0 || ticket.created_by === user?.id) && (
              <Card className="shadow-sm no-print">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-slate-500" /> Dosyalar
                      <Badge variant="secondary" className="text-xs ml-1">{ticket.files?.length || 0}/5</Badge>
                    </CardTitle>
                    {ticket.created_by === user?.id && (ticket.files?.length || 0) < 5 && (
                      <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="add-file-btn" className="h-8">
                        <Upload className="w-3 h-3 mr-1" /> Ekle
                      </Button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleFileUpload} />
                </CardHeader>
                <CardContent>
                  {ticket.files?.length > 0 ? (
                    <div className="space-y-2">{ticket.files.map(f => (
                      <div key={f.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center"><FileText className="w-4 h-4 text-blue-600" /></div>
                          <div>
                            <span className="text-sm font-medium text-slate-900">{f.original_name}</span>
                            <span className="text-xs text-slate-400 ml-2">({(f.size / 1024).toFixed(0)} KB)</span>
                          </div>
                        </div>
                        <a href={ticketsAPI.downloadFile(ticketId, f.id)} target="_blank" rel="noopener noreferrer" className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    ))}</div>
                  ) : <p className="text-slate-400 text-center py-6 text-sm">Henüz dosya eklenmemiş</p>}
                </CardContent>
              </Card>
            )}

            {/* Like & Discussion for projects */}
            {isProject && (
              <Card className="shadow-sm no-print">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Topluluk</CardTitle>
                    <Button variant={userLiked ? "default" : "outline"} size="sm" onClick={handleLike} data-testid="like-btn" className={`h-8 ${userLiked ? "bg-blue-600 text-white" : ""}`}>
                      <ThumbsUp className={`w-3.5 h-3.5 mr-1 ${userLiked ? 'fill-white' : ''}`} /> {ticket.like_count || 0}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <h4 className="font-medium text-slate-800 text-sm">Tartışma ({ticket.discussions?.length || 0})</h4>
                  {ticket.discussions?.length > 0 ? (
                    <div className="space-y-3">{ticket.discussions.map(d => (
                      <div key={d.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900">{d.by_name}</span>
                            <Badge variant="outline" className="text-xs h-5">{
                              d.by_title ? d.by_title :
                              d.by_role === 'admin' ? 'Yönetici' :
                              d.by_role === 'genel_sekreter' ? 'Genel Sekreter' :
                              d.by_role === 'meclis_baskani' ? 'Meclis Başkanı' :
                              d.by_role === 'muhtar' ? 'Muhtar' :
                              d.by_role === 'stk' ? 'STK' : 'Vatandaş'
                            }</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{formatRelativeTime(d.at)}</span>
                          {canManageTickets && <button className="p-0.5 rounded text-slate-300 hover:text-red-500 transition-colors" title="Yorumu sil"
                            onClick={async () => {
                              if (!window.confirm('Bu topluluk yorumunu silmek istediginize emin misiniz?')) return;
                              try { await ticketsAPI.deleteDiscussion(ticket.id, d.id); toast.success('Yorum silindi'); loadTicket(); }
                              catch (e) { toast.error(e.response?.data?.detail || 'Yorum silinemedi'); }
                            }}><Trash2 className="w-3 h-3" /></button>}
                          </div>
                        </div>
                        <p className="text-sm text-slate-700">{d.content}</p>
                      </div>
                    ))}</div>
                  ) : <p className="text-slate-400 text-center text-sm py-3">Henüz tartışma yok</p>}
                  <form onSubmit={handleAddDiscussion} className="flex gap-2 pt-3 border-t">
                    <Input placeholder="Proje hakkında görüşünüzü yazın..." value={discussion} onChange={(e) => setDiscussion(e.target.value)} disabled={submittingDiscussion} data-testid="discussion-input" className="h-9" />
                    <Button type="submit" size="sm" disabled={submittingDiscussion || !discussion.trim()} data-testid="submit-discussion-btn" className="h-9 px-3">
                      {submittingDiscussion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Comments */}
            <Card className="shadow-sm no-print">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-500" /> Yorumlar
                  <Badge variant="secondary" className="text-xs ml-1">{ticket.comments?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {ticket.comments?.length > 0 ? (
                  <div className="space-y-3">{ticket.comments.map((c, idx) => (
                    <div key={c.id || idx} className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
                            <span className="text-xs font-bold text-white">{c.by_name?.charAt(0)}</span>
                          </div>
                          <span className="font-medium text-sm text-slate-900">{c.by_name}</span>
                          <Badge variant="outline" className="text-xs h-5">{
                            c.by_title ? c.by_title :
                            c.by_role === 'admin' ? 'Yönetici' :
                            c.by_role === 'genel_sekreter' ? 'Genel Sekreter' :
                            c.by_role === 'meclis_baskani' ? 'Meclis Başkanı' :
                            c.by_role === 'muhtar' ? 'Muhtar' :
                            c.by_role === 'stk' ? 'STK' : 'Vatandaş'
                          }</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{formatRelativeTime(c.at)}</span>
                          {canManageTickets && <button className="p-0.5 rounded text-slate-300 hover:text-red-500 transition-colors" title="Yorumu sil"
                            onClick={async () => {
                              if (!window.confirm('Bu yorumu silmek istediginize emin misiniz?')) return;
                              try { await ticketsAPI.deleteComment(ticket.id, idx); toast.success('Yorum silindi'); loadTicket(); }
                              catch (e) { toast.error(e.response?.data?.detail || 'Yorum silinemedi'); }
                            }}><Trash2 className="w-3 h-3" /></button>}
                        </div>
                      </div>
                      <p className="text-sm text-slate-700 pl-9">{c.content}</p>
                    </div>
                  ))}</div>
                ) : <p className="text-slate-400 text-center py-6 text-sm">Henüz yorum yapılmamış</p>}
                <form onSubmit={handleAddComment} className="flex gap-2 pt-4 border-t">
                  <Input placeholder="Yorum yazın..." value={comment} onChange={(e) => setComment(e.target.value)} disabled={submittingComment} data-testid="comment-input" className="h-9" />
                  <Button type="submit" size="sm" disabled={submittingComment || !comment.trim()} data-testid="submit-comment-btn" className="h-9 px-3">
                    {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Satisfaction */}
            {ticket.status === 'cozuldu' && ticket.created_by === user?.id && !ticket.satisfaction_score && (
              <Card className="shadow-sm border-emerald-200 bg-emerald-50/50 no-print">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-4 h-4" /> Memnuniyet Değerlendirmesi
                  </CardTitle>
                  <CardDescription>Talebiniz çözüldü! Memnuniyetinizi değerlendirin.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-1">{[1,2,3,4,5].map(star => (
                    <button key={star} onClick={() => handleRateSatisfaction(star)} className="p-1.5 hover:bg-emerald-100 rounded-lg transition-colors" data-testid={`rating-star-${star}`}>
                      <Star className={`w-7 h-7 ${satisfaction >= star ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-300'}`} />
                    </button>
                  ))}</div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Right */}
          <div className="space-y-6">
            {/* Info Card */}
            <Card className="shadow-sm print-break">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Detaylar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className={`space-y-3 ${canViewUserDetail && ticket.created_by_email ? 'cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-2 rounded-md transition-colors' : ''}`}
                  onClick={() => {
                    if (canViewUserDetail && ticket.created_by_email) {
                      navigate(`/panel/kullanicilar?q=${encodeURIComponent(ticket.created_by_email)}`);
                    }
                  }}
                  data-testid="ticket-creator-card"
                  title={canViewUserDetail && ticket.created_by_email ? 'Kullanici detayina git' : undefined}
                >
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                        Oluşturan
                        {canViewUserDetail && ticket.created_by_email && (
                          <ChevronRight className="w-3 h-3 text-slate-300" />
                        )}
                      </p>
                      <p className="text-sm text-slate-900 font-medium">{ticket.created_by_name}</p>
                    </div>
                  </div>
                  {ticket.created_by_email && (
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">E-posta</p>
                        <p className="text-sm text-slate-900 truncate font-mono">{ticket.created_by_email}</p>
                      </div>
                    </div>
                  )}
                  {ticket.created_by_tc_masked && (
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">TC Kimlik (maskeli)</p>
                        <p className="text-sm text-slate-900 font-mono" data-testid="ticket-creator-tc">{ticket.created_by_tc_masked}</p>
                      </div>
                    </div>
                  )}
                  {ticket.created_by_phone && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Telefon</p>
                        <p className="text-sm text-slate-900">{ticket.created_by_phone}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-start gap-3 mb-3">
                      <Tag className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Kategori</p>
                        <p className="text-sm text-slate-900 capitalize">{ticket.category || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 mb-3">
                      <AlertCircle className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Öncelik</p>
                        <p className={`text-sm font-semibold ${getPriorityColor(ticket.priority)}`}>{TICKET_PRIORITY[ticket.priority]?.label || 'Normal'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-start gap-3 mb-3">
                      <CalendarDays className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Oluşturulma</p>
                        <p className="text-sm text-slate-900">{formatDateTime(ticket.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Son Güncelleme</p>
                        <p className="text-sm text-slate-900">{formatDateTime(ticket.updated_at)}</p>
                      </div>
                    </div>
                  </div>

                  {ticket.satisfaction_score && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Memnuniyet</p>
                      <div className="flex items-center gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${ticket.satisfaction_score >= s ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />)}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card className="shadow-sm print-break">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" /> Geçmiş
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-4">
                  <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
                  <div className="space-y-4">
                    {ticket.status_history?.map((h, idx) => (
                      <div key={`timeline-${idx}`} className="relative flex gap-3">
                        <div className={`absolute left-[-9px] top-1.5 w-3 h-3 rounded-full border-2 border-white flex-shrink-0 ${idx === 0 ? 'bg-blue-600' : 'bg-slate-300'}`} />
                        <div className="ml-2">
                          <p className="text-sm text-slate-700">{h.details}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{formatRelativeTime(h.at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Forward Dialog */}
        <Dialog open={forwardDialogOpen} onOpenChange={setForwardDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Talebi Yönlendir</DialogTitle><DialogDescription>Bu talebi ilgili Meclis veya Çalışma Grubuna yönlendirin.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="font-semibold mb-2 block">Meclis / Çalışma Grubu Seçin *</Label>
                {loadingGroups ? (
                  <div className="flex items-center gap-2 py-6 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-sm text-slate-500">Organlar yükleniyor...</span>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="forward-organ-tree">
                    {organHierarchy.map((meclis) => {
                      const isMeclisSelected = forwardData.assigned_organ_id === meclis.id;
                      return (
                        <div key={meclis.id} className="border rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setForwardData({ ...forwardData, assigned_organ_id: meclis.id, _selectedName: meclis.name })}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isMeclisSelected ? 'bg-blue-600 text-white' : 'bg-slate-50 hover:bg-slate-100 text-slate-900'}`}
                            data-testid={`forward-meclis-${meclis.id}`}
                          >
                            <ShieldCheck className={`w-4 h-4 flex-shrink-0 ${isMeclisSelected ? 'text-white' : 'text-blue-600'}`} />
                            <span className="text-sm font-semibold">{meclis.name}</span>
                            {isMeclisSelected && <CheckCircle className="w-4 h-4 ml-auto" />}
                          </button>
                          {meclis.children.length > 0 && (
                            <div className="border-t bg-white">
                              {meclis.children.map(g => {
                                const isSelected = forwardData.assigned_organ_id === g.id;
                                return (
                                  <button
                                    key={g.id}
                                    type="button"
                                    onClick={() => setForwardData({ ...forwardData, assigned_organ_id: g.id, _selectedName: g.name })}
                                    className={`w-full flex items-center gap-3 pl-9 pr-3 py-2 text-left transition-colors border-t border-slate-50 ${isSelected ? 'bg-blue-50 text-blue-800' : 'hover:bg-slate-50 text-slate-700'}`}
                                    data-testid={`forward-group-${g.id}`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isSelected ? 'bg-blue-600' : 'bg-slate-300'}`} />
                                    <span className="text-sm">{g.name}</span>
                                    {isSelected && <CheckCircle className="w-3.5 h-3.5 ml-auto text-blue-600" />}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {forwardData.assigned_organ_id && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <Forward className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-blue-800">Seçilen: {forwardData._selectedName}</span>
                    <button type="button" onClick={() => setForwardData({ ...forwardData, assigned_organ_id: '', _selectedName: '' })} className="ml-auto text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <div className="space-y-2"><Label>Not</Label><Textarea value={forwardData.note} onChange={(e) => setForwardData({ ...forwardData, note: e.target.value })} placeholder="Yönlendirme notu..." rows={3} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForwardDialogOpen(false)}>İptal</Button>
              <Button onClick={handleForward} disabled={forwarding || !forwardData.assigned_organ_id} data-testid="submit-forward-btn">{forwarding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Forward className="w-4 h-4 mr-2" />} Yönlendir</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Durum Değiştir</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-change-select"><SelectValue placeholder="Yeni durum seçin" /></SelectTrigger>
                <SelectContent>{Object.entries(TICKET_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>İptal</Button>
              <Button onClick={handleStatusChange} data-testid="confirm-status-btn">Güncelle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export { TicketsListPage, NewTicketPage, NewProjectPage, TicketDetailPage };
